import type { AdditionalField } from '@views/Lists/ListsTypes.d';
import type { DynamicSubjectRow } from '@cwd/Views/Issues/dynamicSubject';

// color is a chart colour token (@shared/modules/chart-colors)
export const STATISTICS_EVENT_TYPES = [
    { key: 'sent', contactType: 'Sent', color: 'eventSent' },
    { key: 'received', contactType: 'Received', color: 'eventReceived' },
    { key: 'openedorclicked', contactType: 'OpenClicked', color: 'eventOpenedOrClicked' },
    { key: 'opened', contactType: 'Opened', color: 'eventOpened' },
    { key: 'clicked', contactType: 'Clicked', color: 'eventClicked' },
    { key: 'conversion', contactType: 'Converted', color: 'eventConverted' },
    { key: 'bounced', contactType: 'Bounced', color: 'eventBounced' },
    { key: 'unsubscribed', contactType: 'Unsubscribed', color: 'eventUnsubscribed' },
    { key: 'inactive', contactType: 'Inactive', color: 'eventInactive' },
] as const;

export function getStatisticsLabelMap(): Record<string, string> {
    return {
        sent: $Resources.IssuesSentLabel,
        received: $Resources.IssuesReceivedPercentageLabel,
        openedorclicked: $Resources.IssuesOpenClickedPercentageLabel,
        opened: $Resources.IssuesOpenedPercentageLabel,
        clicked: $Resources.IssuesClickedPercentageLabel,
        conversion: $Resources.IssuesConvertedPercentageLabel,
        bounced: $Resources.IssuesBouncedPercentageLabel,
        unsubscribed: $Resources.IssuesUnsubscribedPercentageLabel,
        inactive: $Resources.IssuesInactivePercentageLabel,
    };
}

function negateOperator(op: string): string {
    const map: Record<string, string> = {
        eq: 'ne',
        ne: 'eq',
        contains: 'notContains',
        notcontains: 'contains',
        empty: 'notEmpty',
        notempty: 'empty',
    };
    return map[op.toLowerCase()] ?? op;
}

// Map dynamic-subject operator names to the ContactFilter operator keys.
// Dynamic subject uses lowercase ('notcontains', 'notempty'); ContactFilter uses camelCase.
const DYNAMIC_SUBJECT_OP_MAP: Record<string, string> = {
    eq: 'eq',
    ne: 'ne',
    contains: 'contains',
    notcontains: 'notContains',
    empty: 'empty',
    notempty: 'notEmpty',
};

export interface StatisticsPresetFilter {
    fieldName: string;
    operator: string;
    text: string;
}

export function buildDynamicSubjectPresetFilters(rows: DynamicSubjectRow[], availableFields: AdditionalField[], negate: boolean): StatisticsPresetFilter[] {
    const result: StatisticsPresetFilter[] = [];
    for (const row of rows) {
        const fieldName = row.property.replace(/^Contact\./, '');
        if (!availableFields.some((f) => f.name === fieldName)) continue;
        const rawOp = negate ? negateOperator(row.operator) : row.operator;
        const operator = DYNAMIC_SUBJECT_OP_MAP[rawOp.toLowerCase()] ?? rawOp;
        result.push({ fieldName, operator, text: row.value });
    }
    return result;
}

export function getIssueStatisticsContactListHeadings(): Record<string, string> {
    return {
        Sent: $Resources.StatisticsContactListHeadingSent,
        Received: $Resources.StatisticsContactListHeadingReceived,
        Opened: $Resources.StatisticsContactListHeadingOpened,
        OpenClicked: $Resources.StatisticsContactListHeadingOpenClicked,
        Clicked: $Resources.StatisticsContactListHeadingClicked,
        Converted: $Resources.StatisticsContactListHeadingConverted,
        Bounced: $Resources.StatisticsContactListHeadingBounced,
        Unsubscribed: $Resources.StatisticsContactListHeadingUnsubscribed,
        Inactive: $Resources.StatisticsContactListHeadingInactive,
        ClickedLink: $Resources.StatisticsContactListClickedLinkHeading,
        Shared: $Resources.StatisticsContactListSharedHeading,
    };
}
